import React, { createContext, useContext, useState, useEffect } from 'react';
import { Queries, Types } from '~/graphql';
import { UserPaymentMethod } from '~/components/UserPaymentMethod/UserPaymentMethod';

type PaymentMethodContextType = {
    userPaymentMethodsLoading: boolean;
    showUserPaymentMethods: boolean;
    userPaymentMethodsOptions: Array<{
        id: string;
        label: React.ReactNode;
        type: Types.UserPaymentMethodType | null | undefined;
    }>;
    handleUserPaymentMethodChange: (value: string | boolean) => void;
    handleAddNewPaymentMethod: () => void;
    paymentMethod: Types.PaymentMethod | null;
    setPaymentMethod: (paymentMethod: Types.PaymentMethod | null) => void;
    chosenUserPaymentMethod: string | null;
    isStripeFormVisible: boolean;
};

type PaymentMethodProviderProps = {
    children: React.ReactNode;
    userId?: Types.UUID;
    country: CountryCode3;
};

const PaymentMethodContext = createContext<PaymentMethodContextType | null>(null);

export const PaymentMethodProvider = ({
    children,
    userId,
    country
}: PaymentMethodProviderProps) => {
    const [userPaymentMethods, setUserPaymentMethods] = useState<Array<Types.UserPaymentMethodR>>(
        []
    );
    const [chosenUserPaymentMethod, setChosenUserPaymentMethod] = useState<string | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<Types.PaymentMethod | null>(null);
    const [isStripeFormVisible, setIsStripeFormVisible] = useState(false);

    const { data, loading: userPaymentMethodsLoading } = Queries.useUserPaymentMethodsQuery({
        variables: { userId: userId as Types.UUID, requestFromCountry: country },
        skip: !userId
    });

    const showUserPaymentMethods = !userPaymentMethodsLoading && userPaymentMethods.length > 0;

    // Setting option that is selected by default once data is loaded
    useEffect(() => {
        const defaultMethod = Types.UserPaymentMethodType.CreditCard;
        const filteredPaymentMethods =
            data?.userPaymentMethods.filter(
                (paymentMethod: Types.UserPaymentMethodR) => paymentMethod.type === defaultMethod
            ) || [];
        const defaultPaymentMethod = filteredPaymentMethods.find(
            (item: Types.UserPaymentMethodR) => item?.default
        );

        const chosenPaymentMethod =
            defaultPaymentMethod?.reference || filteredPaymentMethods?.[0]?.reference || null;

        setUserPaymentMethods(filteredPaymentMethods || []);
        setChosenUserPaymentMethod(chosenPaymentMethod || null);
        setIsStripeFormVisible(!chosenPaymentMethod);
    }, [data]);

    const userPaymentMethodsOptions = userPaymentMethods
        .sort((a, b) => (a.default ? -1 : b.default ? 1 : 0))
        .map((item) => ({
            id: item.reference || '',
            label: <UserPaymentMethod userPaymentMethod={item} />,
            type: item.type
        }));

    const handleUserPaymentMethodChange = (value: string | boolean) => {
        if (typeof value === 'string') {
            setChosenUserPaymentMethod(value);
            setIsStripeFormVisible(false);
        } else {
            throw new Error(`Value ${value} is not a string`);
        }
    };

    const handleAddNewPaymentMethod = () => {
        setChosenUserPaymentMethod(null);
        setIsStripeFormVisible(true);
    };

    const contextValue = {
        userPaymentMethodsLoading,
        showUserPaymentMethods,
        userPaymentMethodsOptions,
        handleUserPaymentMethodChange,
        handleAddNewPaymentMethod,
        chosenUserPaymentMethod,
        isStripeFormVisible,
        paymentMethod,
        setPaymentMethod
    };

    return (
        <PaymentMethodContext.Provider value={contextValue}>
            {children}
        </PaymentMethodContext.Provider>
    );
};

export const usePaymentMethodContext = () => {
    const context = useContext(PaymentMethodContext);
    if (!context) {
        throw new Error('usePaymentMethodContext must be used within a PaymentMethodProvider');
    }
    return context;
};
